import { useState } from 'react';
import { Download, X, Share, Plus, ChevronRight } from 'lucide-react';
import { usePWA } from '../context/PWAContext';
import { useLanguage } from '../context/LanguageContext';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

// iOS Install Instructions Modal
const IOSInstallModal = ({ isOpen, onClose }) => {
  const { getIOSInstallInstructions } = usePWA();
  const instructions = getIOSInstallInstructions();

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      <div className="relative w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative px-6 py-5 bg-gradient-to-r from-indigo-600 to-purple-600">
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="ios-install-modal-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{instructions.title}</h2>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="p-4 space-y-3">
          {instructions.steps.map((step, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-xl"
            >
              <div className="flex-shrink-0 w-7 h-7 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {index + 1}
              </div>
              <p className="text-sm text-zinc-300 pt-0.5">{step}</p>
            </div>
          ))}
        </div>

        {/* Visual hint for Share button */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-center gap-2 p-3 bg-zinc-800 rounded-xl border border-zinc-700">
            <div className="flex items-center gap-1 text-zinc-400 text-xs">
              <Share className="w-4 h-4" />
              <ChevronRight className="w-3 h-3" />
              <Plus className="w-4 h-4" />
              <span className="ml-1 text-zinc-500">Home Screen</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Main PWA Install Button for Header (Mobile)
export const PWAInstallButton = ({ className = '' }) => {
  const { 
    isPWAInstalled, 
    canInstall, 
    platform, 
    installPWA, 
    shouldShowInstallButton 
  } = usePWA();
  const { language } = useLanguage();
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Don't render if PWA is already installed
  if (isPWAInstalled) {
    return null;
  }

  // For iOS, show the button to display instructions
  // For others, only show if canInstall is true
  const shouldShow = platform === 'ios' || shouldShowInstallButton;

  if (!shouldShow) {
    return null;
  }

  const handleInstall = async () => {
    if (platform === 'ios') {
      // Show iOS instructions modal
      setShowIOSModal(true);
      return;
    }

    setIsInstalling(true);
    try {
      const result = await installPWA();
      
      if (result.success) {
        toast.success(
          language === 'es' 
            ? '¡DamyxIA instalado correctamente!' 
            : 'DamyxIA installed successfully!'
        );
      } else if (result.outcome === 'dismissed') {
        toast.info(
          language === 'es' 
            ? 'Instalación cancelada' 
            : 'Installation cancelled'
        );
      }
    } catch (error) {
      console.error('Install error:', error);
      toast.error(
        language === 'es' 
          ? 'Error al instalar la app' 
          : 'Error installing app'
      );
    } finally {
      setIsInstalling(false);
    }
  };

  const buttonText = language === 'es' ? 'Instalar' : 'Install';

  return (
    <>
      <button
        onClick={handleInstall}
        disabled={isInstalling}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 
          bg-gradient-to-r from-indigo-600 to-purple-600 
          hover:from-indigo-500 hover:to-purple-500
          text-white text-xs font-medium
          rounded-full
          shadow-lg shadow-indigo-500/25
          transition-all duration-200
          active:scale-95
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
        data-testid="pwa-install-btn"
        title={language === 'es' ? 'Instalar aplicación' : 'Install app'}
      >
        <Download className={`w-3.5 h-3.5 ${isInstalling ? 'animate-bounce' : ''}`} />
        <span className="hidden xs:inline">{buttonText}</span>
      </button>

      {/* iOS Instructions Modal */}
      <IOSInstallModal 
        isOpen={showIOSModal} 
        onClose={() => setShowIOSModal(false)} 
      />
    </>
  );
};

// Compact version for very small screens
export const PWAInstallButtonCompact = ({ className = '' }) => {
  const { isPWAInstalled, platform, installPWA, shouldShowInstallButton } = usePWA();
  const { language } = useLanguage();
  const [showIOSModal, setShowIOSModal] = useState(false);

  if (isPWAInstalled) return null;
  
  const shouldShow = platform === 'ios' || shouldShowInstallButton;
  if (!shouldShow) return null;

  const handleInstall = async () => {
    if (platform === 'ios') {
      setShowIOSModal(true);
      return;
    }
    await installPWA();
  };

  return (
    <>
      <button
        onClick={handleInstall}
        className={`
          p-2 rounded-lg
          bg-gradient-to-r from-indigo-600 to-purple-600 
          hover:from-indigo-500 hover:to-purple-500
          text-white
          shadow-lg shadow-indigo-500/25
          transition-all duration-200
          active:scale-95
          ${className}
        `}
        data-testid="pwa-install-btn-compact"
        title={language === 'es' ? 'Instalar aplicación' : 'Install app'}
      >
        <Download className="w-4 h-4" />
      </button>

      <IOSInstallModal 
        isOpen={showIOSModal} 
        onClose={() => setShowIOSModal(false)} 
      />
    </>
  );
};

export default PWAInstallButton;
